import dayjs from 'dayjs';
import { Pipe, PipeTransform } from '@angular/core';

export function truncateText(value: string, maxLength: number): string {
  if (!value) return '';
  return value.length > maxLength ? value.slice(0, maxLength) + '...' : value;
}

/**
 * Formats a quarter value to standard format (e.g., 'Q1', 'Q2', etc.)
 * @param quarter - The quarter to format (number or string)
 * @returns The formatted quarter string
 */
export function formatQuarter(quarter: string | number): string {
  // Import constants directly to avoid circular dependencies
  const QUARTER = {
    FORMAT_REGEX: /^Q[1-4]$/,
    NUMBER_REGEX: /^[1-4]$/,
    PREFIX: 'Q',
    MIN: 1,
    MAX: 4
  };

  if (typeof quarter === 'number') {
    const q = Math.min(Math.max(QUARTER.MIN, quarter), QUARTER.MAX);
    return `${QUARTER.PREFIX}${q}`;
  }

  if (QUARTER.FORMAT_REGEX.test(quarter)) {
    return quarter;
  }

  if (QUARTER.NUMBER_REGEX.test(quarter)) {
    return `${QUARTER.PREFIX}${quarter}`;
  }

  return getCurrentQuarter();
}

/**
 * Gets the current quarter based on the current month
 * @returns The current quarter string (e.g., 'Q1', 'Q2', etc.)
 */
export function getCurrentQuarter(): string {
  const month = new Date().getMonth();
  const quarterNum = Math.floor(month / 3) + 1;
  return `Q${quarterNum}`;
}

/**
 * Gets the current year
 * @returns The current year as a number
 */
export function getCurrentYear(): string {
  return new Date().getFullYear().toString();
}

/**
 * Case-insensitive string comparator for sorting
 * @param a - First string to compare
 * @param b - Second string to compare
 * @returns Number indicating sort order (-1, 0, 1)
 */
export function caseInsensitiveStringComparator(a: string, b: string): number {
  const stringA = (a || '').toLowerCase();
  const stringB = (b || '').toLowerCase();
  return stringA.localeCompare(stringB);
}

export const filterData = (data: any[], filters: any) => {
  return data.filter((item) => {
    return Object.keys(filters).every((key) => {
      const filterValue = filters[key];
      if (!filterValue) return true;

      if (key === 'filterByMonth' && filterValue?.value) {
        const [month, year] = filterValue.value.split('-');
        if (!item[key]) return false;
        const date = new Date(item[key]);
        const itemMonth = (date.getMonth() + 1).toString();
        const itemYear = date.getFullYear().toString();
        return itemMonth === month && itemYear === year;
      }

      if (typeof filterValue === 'string') {
        if (key === 'yearsOfExperience') {
          const itemValue = item[key];
          if (!itemValue) return false;
          if (typeof itemValue === 'string' && itemValue.toLowerCase() === filterValue.toLowerCase()) {
            return true;
          }
          const filterNumber = parseFloat(filterValue.replace(/[^\d.]/g, ''));
          const itemNumber = parseFloat(itemValue.toString().replace(/[^\d.]/g, ''));
          if (!isNaN(filterNumber) && !isNaN(itemNumber)) {
            return itemNumber === filterNumber;
          }
          return (
            typeof itemValue === 'string' &&
            itemValue.toLowerCase().includes(filterValue.toLowerCase())
          );
        }

        if (key === 'location') {
          const itemValue = item[key];
          if (Array.isArray(itemValue)) {
            return itemValue.some((loc: string) =>
              typeof loc === 'string' &&
              loc.toLowerCase().includes(filterValue.toLowerCase())
            );
          } else {
            return (
              typeof itemValue === 'string' &&
              itemValue.toLowerCase().includes(filterValue.toLowerCase())
            );
          }
        }

        return (
          typeof item[key] === 'string' &&
          item[key]?.toLowerCase()?.includes(filterValue.toLowerCase())
        );
      } else if (filterValue?.value !== undefined) {
        if (Array.isArray(item[key])) {
          return item[key]?.some((location: string) =>
            typeof location === 'string' &&
            (
              location.trim().toLowerCase() === filterValue?.value?.trim().toLowerCase()
            )
          );
        } else if (typeof filterValue.value === 'number') {
          return item[key] === filterValue.value;
        } else {
          return (
            typeof item[key] === 'string' &&
            item[key]?.toLowerCase()?.includes(filterValue?.value.toLowerCase())
          );
        }
      } else {
        if (filterValue?.length > 0) {
          return filterValue?.some(
            (filter: { selected: boolean; value: string }) =>
              filter?.selected &&
              typeof item[key] === 'string' &&
              item[key]?.toLowerCase() === filter?.value?.toLowerCase(),
          );
        }
      }
      return true;
    });
  });
};

export function formatSingleDate(date: string | Date, format: string): string {
  return dayjs(date).format(format);
}

export function formatDate(dateStr: string, format: string = 'DD-MM-YYYY'): string {
  if (!dateStr || typeof dateStr !== 'string') return ''; // Handle empty or non-string dates
  const date = new Date(dateStr);

  if (isNaN(date.getTime())) {
    return ''; // Return an empty string if the date is invalid
  }

  const day = ('0' + date.getDate()).slice(-2);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const year = date.getFullYear();
  return format === 'DD-MM-YYYY' ? `${day}-${month}-${year}` : `${year}-${month}-${day}`;
}

/**
 * Formats time from 24-hour format to 12-hour format with AM/PM
 * @param time - The time string in HH:MM or HH:MM:SS format
 * @returns The formatted time string in 12-hour format (e.g., "09:00 AM", "02:30 PM")
 */
export function formatTimeTo12Hour(time: string): string {
  if (!time) return '';

  const timeParts = time.split(':');
  if (timeParts.length < 2) return time;
  
  let hours = parseInt(timeParts[0]);
  const minutes = timeParts[1];
  
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; 
  
  return `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
}

export const chartColors = {
  backgroundWhite: '#ffffff',
  textDark: '#1a1a1a',
  mintGreen: '#80bbad',
  darkOrange: '#ff543e',
  dividerGrey: '#e0e0e0',
  tooltipBackground: '#ffffff',
  tooltipTitle: '#000000',
  tooltipBody: '#000000',
};


export function generateLast12Months(): {
  label: string;
  value: string;
}[] {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i);
    const monthLabel = monthDate.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });
    const monthValue = monthDate.getMonth() + 1;
    const yearValue = monthDate.getFullYear();
    months.push({ label: monthLabel, value: `${monthValue}-${yearValue}`});
  }
  return months;
}

@Pipe({ name: 'truncateText' })
export class TruncateTextPipe implements PipeTransform {
  transform(value: string, maxLength: number): string {
    return truncateText(value, maxLength);
  }
}

export const RTODrillData = {
  TABLE_CONFIG: {
    COLUMN_DEFINITIONS: [
      {
        field: 'name',
        headerName: 'Name',
        filterParams: { hideMenu: true },
        sortParams: { enabled: true },
      },
      {
        field: 'reportingManager',
        headerName: 'Reporting Manager',
        filterParams: { hideMenu: true },
        sortParams: { enabled: true },
      },
      {
        field: 'doj',
        headerName: 'DOJ',
        filterParams: { hideMenu: true },
        sortParams: { enabled: true },
      },
      {
        field: 'location',
        headerName: 'Location',
        filterParams: { hideMenu: true },
        sortParams: { enabled: true },
      },
      {
        field: 'lob',
        headerName: 'LoB',
        filterParams: { hideMenu: true },
        sortParams: { enabled: true },
      },
      {
        field: 'rto',
        headerName: 'RTO',
        filterParams: { hideMenu: true },
        sortParams: { enabled: true },
      },
      {
        field: 'daysPresent',
        headerName: 'Days Present',
        filterParams: { hideMenu: true },
        sortParams: { enabled: false },
      },
    ],
    ICON_FONT_SIZE: 1.3,
    PAGINATION: {
      ENABLED: true,
      PAGE_SIZE: 10,
      PAGE_SIZE_OPTIONS: [5, 10, 20]
    }
  },

  LABELS: {
    SEARCH: 'Search',
    FILTER: 'Filter',
    CLOSE: 'Close',
    LEGEND: 'Legend',
    DAYS: 'days',
    VIEW_RTO_CALENDAR: 'View RTO Calendar',
    RTO_CALENDAR: 'RTO Calendar',
    RTO_ATTENDANCE_TRACKING: 'RTO Attendance Tracking',
    NO_RECORDS: 'No Records Found',
    REGULAR: 'Regular'
  },

  NAVIGATION: {
    PREVIOUS: 'Previous',
    TODAY: 'Today',
    NEXT: 'Next',
    MINIMIZE: 'Minimize'
  },

  BREADCRUMBS: {
    VENDOR_MANAGEMENT: 'Vendor Management',
    VENDOR_LIST: 'Vendor List',
    CONTRACTOR_LIST: 'Contractor List',
    LIST_OF_CONTRACTORS: 'List of Contractors'
  },

  BUTTONS: {
    TYPES: {
      PRIMARY: 'Primary',
      SECONDARY: 'Secondary',
      TEXT_BUTTON: 'TextButton',
      LIGHT: 'Light'
    },
    SIZES: {
      SMALL: 'Small',
      MEDIUM: 'Medium',
      LARGE: 'Large'
    },
    ICON_POSITIONS: {
      LEFT: 'Left',
      RIGHT: 'Right'
    }
  },

  CALENDAR: {
    DAY_HEADERS: [
      'Sun',
      'Mon',
      'Tue',
      'Wed',
      'Thu',
      'Fri',
      'Sat'
    ],
    DAYS_IN_GRID: 42,
    WEEKEND_DAYS: [0, 6],
    ATTENDANCE_TYPES: {
      OFFICE: 'office',
      REMOTE: 'remote',
      ABSENT: 'absent'
    },
    ATTENDANCE_ICONS: {
      OFFICE: '🏢',
      REMOTE: '🏠',
      ABSENT: '❌'
    },
    RTO_POLICIES: {
      THREE_DAYS: '3 Days',
      TEN_DAYS: '10 Days'
    }
  },

  ATTENDANCE: {
    OFFICE_DAYS: 'Office Days',
    REMOTE_DAYS: 'Remote Days',
    ABSENT_DAYS: 'Absent Days',
    HOLIDAY_DAYS: 'Public Holidays'
  },

  CONTRACTOR_INFO: {
    REPORTING_MANAGER: 'Reporting Manager',
    DOJ: 'DOJ',
    EMPLOYEE_ID: 'Employee ID'
  },

  TIME: {
    OFFICE_IN: '9:00 AM',
    OFFICE_OUT: '6:00 PM'
  },

  ACTIONS: {
    DOWNLOAD_REPORT: 'Download Report',
    VIEW_DETAILS: 'View Details',
    EXPORT_DATA: 'Export Data'
  },

  MESSAGES: {
    ERROR: {
      NO_DATA_AVAILABLE: 'No contractors found',
      FAILED_TO_LOAD: 'Failed to load data',
      SEARCH_ERROR: 'Search failed',
      NETWORK_ERROR: 'Network connection error'
    },
    SUCCESS: {
      DATA_LOADED: 'Data loaded successfully',
      REPORT_DOWNLOADED: 'Report downloaded successfully',
      ACTION_COMPLETED: 'Action completed successfully'
    }
  },

  STATIC_CONFIG: {
    IMAGE_PATHS: {
      NO_DATA: 'assets/img/no-data-found.png'
    },
    DEFAULT_FILTERS: [
      { label: 'Regular', type: 'regular' }
    ],
    MODAL_ACTIONS: [
      {
        label: 'Minimize',
        size: 'Small',
        type: 'TextButton',
        icon: 'minimize',
        action: 'minimize'
      }
    ]
  },

  MOCK_DATA: {
    CONTRACTORS: [
      {
        id: 'EMP001',
        name: 'Raghavan',
        role: 'Sr UX Designer',
        reportingManager: 'Alfredo Dorwart',
        doj: '1/4/2024',
        location: 'Hyd',
        lob: 'A&T',
        rto: '3 Days',
        daysPresent: '3 Days',
        status: 'Active',
      },
      {
        id: 'EMP002',
        name: 'Srinivas',
        role: 'Sr UX Designer',
        reportingManager: 'Alfredo Dorwart',
        doj: '1/4/2024',
        location: 'Hyd',
        lob: 'Salesforce',
        rto: '10 Days',
        daysPresent: '10 Days',
        status: 'Active',
      },
      {
        id: 'EMP003',
        name: 'Sadhesh',
        role: 'Sr UX Designer',
        reportingManager: 'Alfredo Dorwart',
        doj: '1/4/2024',
        location: 'Hyd',
        lob: 'GWS',
        rto: '10 Days',
        daysPresent: '10 Days',
        status: 'Active',
      }
    ]
  },

  UTILS: {
    formatTooltip: {
      weekend: (dayName: string, dateStr: string) =>
        `${dayName}, ${dateStr} - Weekend`,
      office: (dayName: string, dateStr: string, timeIn: string, timeOut: string) =>
        `${dayName}, ${dateStr} - Present in Office (${timeIn} - ${timeOut})`,
      remote: (dayName: string, dateStr: string) =>
        `${dayName}, ${dateStr} - Remote Work`,
      absent: (dayName: string, dateStr: string) =>
        `${dayName}, ${dateStr} - Absent`,
      default: (dayName: string, dateStr: string) => `${dayName}, ${dateStr}`
    },

    getCalendarNavButtons: () => [
      {
        label: 'Previous',
        size: 'Small',
        type: 'Secondary',
        icon: 'chevron_left',
        iconPosition: 'Left',
        action: 'previous'
      },
      {
        label: 'Today',
        size: 'Small',
        type: 'Primary',
        icon: 'today',
        action: 'today'
      },
      {
        label: 'Next',
        size: 'Small',
        type: 'Secondary',
        icon: 'chevron_right',
        iconPosition: 'Right',
        action: 'next'
      }
    ]
  }
};

export function generateLast12MonthsVendor(): {
  value: string;
  label: string;
  month: string;
  year: string;
  selected?: boolean;
}[] {
  const now = new Date();
  const monthOptions = [];
  for (let i = 0; i < 12; i++) {
    const monthDate = new Date(now.getFullYear(), now.getMonth() - i);
    const monthLabel = monthDate.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });
    const monthValue = `${(monthDate.getMonth() + 1).toString().padStart(2, '0')}, ${monthDate.getFullYear()}`;
    monthOptions.push({
      label: monthLabel,
      value: monthValue,
      month: (monthDate.getMonth() + 1).toString().padStart(2, '0'),
      year: monthDate.getFullYear().toString(),
      selected: i === 0,
    });
  }
  return monthOptions;
}

export function onMonthChange(
  event: any,
  monthOptions: { value: string; label: string; month: string; year: string }[],
  errorHandler: any,
  dataLoader: () => void,
  selectedMonth: { value: string },
  selectedYear: { value: string },
  preSelectedValue: { value: string }
): void {
  let selectedValue: string | undefined;

  if (typeof event === 'string') {
    const monthOption = monthOptions.find((option) => option.label === event);
    selectedValue = monthOption ? monthOption.value : undefined;
  } else if (event.value) {
    selectedValue = event.value;
  }

  if (!selectedValue || !monthOptions.some((option) => option.value === selectedValue)) {
    errorHandler.showError('Invalid month selected');
    return;
  }

  const selectedOption = monthOptions.find((option) => option.value === selectedValue);
  if (selectedOption) {
    selectedMonth.value = selectedOption.month;
    selectedYear.value = selectedOption.year;
    preSelectedValue.value = selectedOption.value;

    dataLoader();
  }
}

export function generateQuarterOptions(currentYear: number, numberOfYears: number) {
  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
  const options: any[] = [];

  const currentMonth = new Date().getMonth();
  const currentQuarterIndex = Math.floor(currentMonth / 3);
  const currentQuarter = quarters[currentQuarterIndex];

  for (let year = currentYear; year > currentYear - numberOfYears; year--) {
    quarters?.forEach(q => {
      const isCurrent = (year === currentYear && q === currentQuarter);
      options?.push({
        label: `${q} ${year}`,
        value: `${q}-${year}`,
        quarter: q,
        year: year.toString(),
        selected: isCurrent
      });
    });
  }

  return options;
}

/**
 * Checks if user has level 2 or level 3 access
 * @param employeeLevel - The employee level as string or number
 * @returns true if level is 2 or 3, false otherwise
 */
export function hasLevel2Or3Access(employeeLevel: string | number | undefined | null): boolean {
  if (!employeeLevel) return false;

  const level = typeof employeeLevel === 'string' ? parseInt(employeeLevel, 10) : employeeLevel;
  return !isNaN(level) && (level === 2 || level === 3);
}

export function getInitials(name: string): string {
  if (!name || !name.trim()) return '';
  const parts = name.trim().split(' ').filter(part => part.length > 0);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function capitalizeWords(text: string): string {
  if (!text) return '';
  return text
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}